// 🔴 P-MARIO-03 — ENTRÉE V1 : résolution client → lieu → dossier, puis relevé.
//
// 🔑 FRONTIÈRE L1 (décision CTO P-MARIO-11) : `chantier` ⛔ ne lit PAS les clients,
//    lieux et dossiers d'un autre domaine. C'est L'ÉCRAN qui ORCHESTRE les routes
//    EXISTANTES de ces domaines, puis confie le dossier choisi à `/api/chantier/*`.
//    (`pages/chantier/` reste réservé à `/api/chantier/*` — d'où ce module à part.)
// 🔑 Session OmniHub : le tenant ⛔ n'est JAMAIS envoyé, le serveur le lit dans la session.
// ⛔ AUCUNE écriture tant que l'humain n'a pas confirmé : `chercher*` et `lieux*` sont
//    des LECTURES ; `confirmer` est le SEUL point d'écriture.
import { apiBureau } from "../chantier/api";

const API = process.env.REACT_APP_BACKEND_URL || "";
const e = encodeURIComponent;

function entetes(json) {
  let jeton = null;
  try { jeton = localStorage.getItem("session_token"); } catch { /* indisponible */ }
  return { ...(json ? { "Content-Type": "application/json" } : {}),
           ...(jeton ? { Authorization: `Bearer ${jeton}` } : {}) };
}

async function appel(chemin, { methode = "GET", corps } = {}) {
  const r = await fetch(`${API}${chemin}`, {
    method: methode, credentials: "include", headers: entetes(corps !== undefined),
    ...(corps !== undefined ? { body: JSON.stringify(corps) } : {}),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) {
    const d = j?.detail;
    const err = new Error(typeof d === "string" ? d : (d?.message || `Échec (HTTP ${r.status})`));
    err.statut = r.status;
    throw err;
  }
  return j;
}

/** Minuscules sans accents — pour COMPARER, ⛔ pas pour afficher. */
export function plat(t) {
  return String(t || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
}
const echapper = (t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Dictée « Client, adresse… » ⇒ { client, lieu } (1re virgule). Les deux champs restent
 *  MODIFIABLES : ce découpage est une proposition, ⛔ une décision. */
export function decouperDictee(texte) {
  const t = String(texte || "").trim();
  const i = t.indexOf(",");
  return i < 0 ? { client: t, lieu: "" } : { client: t.slice(0, i).trim(), lieu: t.slice(i + 1).trim() };
}

/** Clients qui correspondent (LECTURE). Phrase entière d'abord, puis mot à mot
 *  (« Syndic ABC » doit retrouver « ABC »). Dédoublonné, ⛔ trié par l'écran. */
export async function chercherClients(texte) {
  const q = String(texte || "").trim();
  if (q.length < 2) return [];
  const vus = new Map();
  const garder = (liste) => (liste || []).forEach((c) => { if (c?.id && !vus.has(c.id)) vus.set(c.id, c); });
  garder(await appel(`/api/metier/clients/search?q=${e(echapper(q))}&limit=10`));
  if (!vus.size) {
    for (const mot of q.split(/[\s,]+/).filter((m) => m.length >= 3)) {
      garder(await appel(`/api/metier/clients/search?q=${e(echapper(mot))}&limit=10`));
    }
  }
  return [...vus.values()];
}

/** Pertinence d'un lieu pour ce qui a été dicté : nombre de mots retrouvés. */
export function scoreLieu(lieu, texte) {
  const cible = plat([lieu.nom_site, lieu.adresse, lieu.code_postal, lieu.ville].filter(Boolean).join(" "));
  return plat(texte).split(/[\s,]+/).filter((m) => m.length >= 2 && cible.includes(m)).length;
}

/** Lieux du client (LECTURE), chacun avec ses dossiers — les plus pertinents d'abord. */
export async function lieuxDuClient(clientId, texte) {
  const j = await appel(`/api/metier/clients/${e(clientId)}/sites`);
  return (j.sites || []).map((s) => ({ ...s, score: scoreLieu(s, texte) }))
    .sort((a, b) => b.score - a.score);
}

/** Capacités du tenant DE LA SESSION, lues à la source d'OmniHub quand le cache local est
 *  vide (application ouverte directement). ⚠️ SEUL le tenant courant du serveur
 *  (`current_tenant_slug`) fait foi — ⛔ repli sur « le premier » ou « par défaut ». */
export async function capacitesDeLaSession() {
  const j = await appel("/api/platform/user/tenants");
  const t = (j.tenants || []).find((x) => x.slug && x.slug === j.current_tenant_slug);
  return { profil: t?.profil_chantier || null, activite: t?.dossier_activite || null };
}

export async function mesVisitesEnCours() {
  return (await appel("/api/chantier/sessions/mes-en-cours")).releves || [];
}

/** 🔴 LE SEUL POINT D'ÉCRITURE — appelé APRÈS la confirmation explicite.
 *  lieu : { id } existant OU { nouveau: { adresse, ville, code_postal?, nom_site? } }
 *  dossier : { id } existant OU null (⇒ nouveau dossier sur ce lieu). */
export async function confirmer({ client, lieu, dossier, activite, profilCode, libelles }) {
  let lieuId = lieu.id;
  if (!lieuId) {
    const cree = await appel(`/api/metier/clients/${e(client.id)}/sites`, { methode: "POST", corps: lieu.nouveau });
    lieuId = cree.id;
  }
  let dossierId = dossier?.id;
  let reference = dossier?.reference;
  if (!dossierId) {
    const adr = lieu.nouveau || lieu;
    const d = await appel("/api/metier/dossiers", { methode: "POST", corps: {
      client_id: client.id, site_id: lieuId, activite,
      adresse_travaux: adr.adresse || undefined, code_postal_travaux: adr.code_postal || undefined,
      ville_travaux: adr.ville || undefined,
    } });
    const dd = d.dossier || d;
    dossierId = dd.id; reference = dd.reference;
  }
  // 🔴 P-MARIO-15 — LE MÊME mécanisme que « Nouvelle visite » de la fiche dossier :
  //    `apiBureau(dossier).ouvrir` (⛔ un second appel écrit ici). `contexte` rend
  //    « Reprendre » lisible SANS que `chantier` lise un autre domaine.
  const releve = await apiBureau(dossierId).ouvrir(profilCode,
    { client: libelles.client, lieu: libelles.lieu, dossier: reference || null });
  return { releve, dossierId, reference };
}
